/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { Button } from '@/components/button';
import { Col, Form, Row, Select } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiFetcher } from '@/utils/api-service';
import { APIPrefix } from '@/enums/api.enum';
import { transformerArray } from '@/redux/transformer';
import { capitalizeFirstLetter } from '@/utils/common';

type UserClientSearcherProps = {
  onSearch: (values: FilterUserClient) => void;
};

export const UserClientSearcher = ({ onSearch }: UserClientSearcherProps) => {
  const apiFetcher = useMemo(
    () => new ApiFetcher(currentCtx.appEnv.apiHost, APIPrefix.Client),
    []
  );

  const apiFetcherAdmin = useMemo(
    () => new ApiFetcher(currentCtx.appEnv.apiHost, APIPrefix.Admin),
    []
  );
  const [form] = Form.useForm();

  const [genders, setGenders] = useState<Gender[]>([]);
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [statusClient, setStatusClient] = useState<StatusCLient[]>([]);
  const [selectedStatusValue, setSelectedStatusValue] = useState<
    string | string[]
  >(['all']);

  const genderOptions = useMemo(
    () => [
      { value: '', label: 'Tất cả' },
      { value: '3', label: 'Không có thông tin giới tính' },
      ...genders.map((gender) => ({
        value: gender.id,
        label: capitalizeFirstLetter(gender.name)
      }))
    ],
    [genders]
  );

  const provinceOptions = useMemo(
    () => [
      { value: '', label: 'Tất cả' },
      { value: '0', label: 'Không có thông tin ' },
      ...provinces.map((province) => ({
        value: province.name.toLowerCase(),
        label: province.name
      }))
    ],
    [provinces]
  );

  const statusClientOptions = useMemo(() => {
    return [
      { value: '', label: 'Tất cả' },
      ...statusClient.map((status) => ({
        value: status.type,
        label: status.type === 'unblock' ? 'Đã kích hoạt' : 'Đã bị khoá'
      }))
    ];
  }, [statusClient]);

  const fetchGenders = useCallback(async () => {
    const response = await apiFetcher.get(
      'configuration/gender?offset=1&limit=9999999'
    );
    setGenders(transformerArray(response.data.listData));
  }, [apiFetcher]);

  const fetchProvinces = useCallback(async () => {
    const response = await apiFetcher.get('configuration/province');
    setProvinces(transformerArray(response.data));
  }, [apiFetcher]);

  const fetchStatusCLient = useCallback(async () => {
    const response = await apiFetcherAdmin.get('user/list-types');
    setStatusClient(transformerArray(response.data.listData));
  }, [apiFetcherAdmin]);

  const ageData = useMemo(
    () => [
      { value: 200, label: 'Không có thông tin' },
      ...Array.from({ length: 101 }, (_, i: number) => ({
        value: Number(i),
        label: i
      }))
    ],
    []
  );

  const handleSearch = () => {
    const values = form.getFieldsValue();
    onSearch(values);
  };

  const handleReset = () => {
    form.resetFields();
    handleSearch();
  };

  useEffect(() => {
    fetchGenders();
  }, [fetchGenders]);

  useEffect(() => {
    fetchProvinces();
  }, [fetchProvinces]);

  useEffect(() => {
    fetchStatusCLient();
  }, [fetchStatusCLient]);

  return (
    <Form
      form={form}
      name='user-client-searcher'
      layout='horizontal'
      labelCol={{ span: 4 }}
      wrapperCol={{ span: 20 }}
    >
      <Row gutter={[24, 0]} align='middle'>
        <Col span={4}>
          <Form.Item
            label='Giới tính'
            name='gender'
            initialValue=''
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
          >
            <Select
              style={{ width: '100%' }}
              allowClear
              placeholder='Chọn giới tính'
              optionFilterProp='label'
              options={genderOptions}
            />
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item
            label='Tỉnh thành'
            name='province'
            initialValue=''
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
          >
            <Select
              style={{ width: '100%' }}
              allowClear
              showSearch
              placeholder='Chọn tỉnh thành'
              optionFilterProp='label'
              options={provinceOptions}
            />
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item
            label='Trạng thái'
            name='status'
            initialValue=''
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
          >
            <Select
              style={{ width: '100%' }}
              allowClear
              placeholder='Chọn trạng thái'
              options={statusClientOptions}
            />
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item
            label='Tuổi'
            name='age'
            initialValue='all'
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
          >
            <Select
              style={{ width: '100%' }}
              allowClear
              placeholder='Tất cả'
              options={[
                {
                  value: 'all',
                  label: 'Tất cả',
                  disabled:
                    !selectedStatusValue.includes('all') &&
                    selectedStatusValue.length > 0
                },
                ...ageData.map((item: any) => ({
                  ...item,
                  disabled: selectedStatusValue.includes('all')
                }))
              ]}
              mode='multiple'
              onChange={(value) => {
                setSelectedStatusValue(value);
              }}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Row gutter={[16, 0]} justify='start'>
            <Col>
              <Button
                onClick={handleReset}
                type='default'
                css={searchBtnStyles}
              >
                Xoá bộ lọc
              </Button>
            </Col>
            <Col>
              <Button
                type='primary'
                css={searchBtnStyles}
                icon={<SearchOutlined />}
                iconPosition='start'
                onClick={handleSearch}
              >
                Tìm kiếm
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
    </Form>
  );
};

const searchBtnStyles = css`
  margin-top: 1.6rem;
`;
